import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Container,
  alpha,
  Chip,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Home as HomeIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  Dashboard as DashboardIcon,
  ContactEmergencyOutlined as EmergencyIcon,
  Logout as LogoutIcon,
  GitHub as GitHubIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import ThemeToggleButton from './ThemeToggleButton';
import LanguageToggle from './LanguageToggle';

interface LayoutProps {
  children: React.ReactNode;
}

const DRAWER_WIDTH_EXPANDED = 280;
const DRAWER_WIDTH_COLLAPSED = 72;

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    // Persistir estado del menú en localStorage
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isAuthenticated = authService.isAuthenticated();
  const { t } = useTranslation();

  // Persistir estado del menú
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(desktopCollapsed));
  }, [desktopCollapsed]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopToggle = () => {
    setDesktopCollapsed(!desktopCollapsed);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/');
  };

  const menuItems = [
    { text: 'Inicio', icon: <HomeIcon />, path: '/', public: true, hideWhenAuth: true },
    { text: 'Iniciar Sesión', icon: <LoginIcon />, path: '/login', public: true, hideWhenAuth: true },
    { text: 'Registrarse', icon: <PersonAddIcon />, path: '/register', public: true, hideWhenAuth: true },
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', public: false },
    { text: 'Información de Emergencia', icon: <EmergencyIcon />, path: '/emergency-info', public: false },
    { text: 'Configuración de Cuenta', icon: <SettingsIcon />, path: '/account-settings', public: false },
  ];

  const drawer = (collapsed = false) => (
    <Box 
      sx={{ 
        width: collapsed ? DRAWER_WIDTH_COLLAPSED : (isMobile ? '100vw' : DRAWER_WIDTH_EXPANDED), 
        height: '100%',
        background: alpha(theme.palette.background.paper, 0.95),
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
      }}
    >
      <Toolbar sx={{ 
        minHeight: { xs: 56, sm: 64 },
        px: collapsed ? 1 : { xs: 2, sm: 3 },
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        background: alpha(theme.palette.primary.main, 0.05),
        justifyContent: collapsed ? 'center' : 'flex-start',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        {/* Espacio vacío para centrar el botón de toggle */}
        <Box sx={{ flex: 1 }} />
        
        {/* Botón de colapsar/expandir solo en desktop */}
        {!isMobile && (
          <IconButton
            onClick={handleDesktopToggle}
            sx={{
              position: 'absolute',
              right: collapsed ? 8 : 12,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 32,
              height: 32,
              background: alpha(theme.palette.primary.main, 0.1),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: alpha(theme.palette.primary.main, 0.15),
                transform: 'translateY(-50%) scale(1.05)',
              },
            }}
          >
            {desktopCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        )}
      </Toolbar>
      
      <List sx={{ px: collapsed ? 0.5 : { xs: 1, sm: 2 }, py: 2 }}>
        {menuItems.map((item) => {
          if (!item.public && !isAuthenticated) return null;
          if (item.hideWhenAuth && isAuthenticated) return null;
          
          const isSelected = location.pathname === item.path;
          
          const listItemButton = (
            <ListItemButton
              selected={isSelected}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              sx={{
                borderRadius: collapsed ? 2 : 3,
                py: collapsed ? 1.5 : { xs: 1.5, sm: 1.2 },
                px: collapsed ? 1 : { xs: 2, sm: 2 },
                mb: 1,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                justifyContent: collapsed ? 'center' : 'flex-start',
                minHeight: 48,
                ...(isSelected ? {
                  background: 'var(--primary-gradient)',
                  color: 'white',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                  transform: collapsed ? 'scale(1.05)' : 'translateX(4px)',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                  ...(!collapsed && {
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: 4,
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: '0 4px 4px 0',
                    },
                  }),
                } : {
                  '&:hover': {
                    background: alpha(theme.palette.primary.main, 0.08),
                    transform: collapsed ? 'scale(1.05)' : 'translateX(2px)',
                  },
                }),
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: collapsed ? 'auto' : { xs: 40, sm: 48 },
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                ...(isSelected && {
                  transform: 'scale(1.1)',
                }),
              }}>
                {item.icon}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{
                    fontSize: { xs: '0.9rem', sm: '0.95rem' },
                    fontWeight: isSelected ? 600 : 500,
                    fontFamily: 'Inter, sans-serif',
                  }}
                />
              )}
            </ListItemButton>
          );

          return (
            <ListItem key={item.text} disablePadding>
              {collapsed && !isMobile ? (
                <Tooltip title={item.text} placement="right">
                  {listItemButton}
                </Tooltip>
              ) : (
                listItemButton
              )}
            </ListItem>
          );
        })}
        
        {isAuthenticated && (
          <ListItem disablePadding sx={{ mt: 3 }}>
            {collapsed && !isMobile ? (
              <Tooltip title="Cerrar Sesión" placement="right">
                <ListItemButton 
                  onClick={handleLogout}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    px: 1,
                    background: alpha(theme.palette.error.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    justifyContent: 'center',
                    minHeight: 48,
                    '&:hover': {
                      background: alpha(theme.palette.error.main, 0.15),
                      transform: 'scale(1.05)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
                    },
                    '& .MuiListItemIcon-root': {
                      color: theme.palette.error.main,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 'auto', justifyContent: 'center' }}>
                    <LogoutIcon />
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            ) : (
              <ListItemButton 
                onClick={handleLogout}
                sx={{
                  borderRadius: 3,
                  py: { xs: 1.5, sm: 1.2 },
                  px: { xs: 2, sm: 2 },
                  background: alpha(theme.palette.error.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: alpha(theme.palette.error.main, 0.15),
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
                  },
                  '& .MuiListItemIcon-root': {
                    color: theme.palette.error.main,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: { xs: 40, sm: 48 } }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Cerrar Sesión"
                  primaryTypographyProps={{
                    fontSize: { xs: '0.9rem', sm: '0.95rem' },
                    fontWeight: 500,
                    fontFamily: 'Inter, sans-serif',
                    color: theme.palette.error.main,
                  }}
                />
              </ListItemButton>
            )}
          </ListItem>
        )}
      </List>
    </Box>
  );

  const drawerWidth = isMobile ? 0 : (desktopCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          zIndex: theme.zIndex.drawer + 1,
          background: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          height: { xs: 56, sm: 64 },
          color: theme.palette.text.primary,
          width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
          ml: isMobile ? 0 : `${drawerWidth}px`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Toolbar sx={{ 
          minHeight: { xs: '56px !important', sm: '64px !important' },
          px: { xs: 2, sm: 3 }
        }}>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="abrir menú"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: 2, 
                p: 1,
                borderRadius: 2,
                background: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  background: alpha(theme.palette.primary.main, 0.15),
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
            {!isMobile && (
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  background: 'var(--primary-gradient)',
                  fontSize: '1.3rem',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                }}
              >
                🚨
              </Avatar>
            )}
            
            <Typography 
              variant="h6" 
              noWrap 
              component="div" 
              sx={{ 
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                fontWeight: 700,
                fontFamily: 'Inter, sans-serif',
                background: 'var(--primary-gradient)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {isMobile ? 'Cuerda.cl' : 'Cuerda.cl - Tu información vital, siempre segura'}
            </Typography>
          </Box>
          
          {/* Controles modernos */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            alignItems: 'center',
            background: alpha(theme.palette.background.paper, 0.1),
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            p: 0.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}>
            <LanguageToggle />
            <ThemeToggleButton />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer para móvil */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            background: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: 'none',
          },
        }}
      >
        {drawer(false)}
      </Drawer>

      {/* Drawer para desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: desktopCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED,
            background: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: 'none',
            borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawer(desktopCollapsed)}
      </Drawer>

      {/* Contenido principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          background: `
            radial-gradient(circle at 20% 80%, ${alpha(theme.palette.primary.main, 0.03)} 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, ${alpha(theme.palette.secondary.main, 0.03)} 0%, transparent 50%),
            ${theme.palette.background.default}
          `,
          position: 'relative',
          width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
          ml: isMobile ? 0 : `${drawerWidth}px`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Toolbar />
        <Container 
          maxWidth="xl" 
          sx={{ 
            py: { xs: 2, sm: 3, md: 4 },
            px: { xs: 2, sm: 3 },
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          <Box className="fade-in-up">
            {children}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Layout; 